import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const VALID_QUESTION_TYPES = [
    'text', 'full_name', 'email', 'phone', 'number', 'url',
    'select', 'choices', 'yes_no_maybe', 'scale',
    'date', 'datetime', 'monetary', 'photo', 'video',
    'weight', 'gender', 'body_structure',
];

function validateTemplateJson(data, t) {
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
        return t('templates.import.errors.notObject');
    }
    if (!data.title || typeof data.title !== 'string') {
        return t('templates.import.errors.missingTitle');
    }
    if (!Array.isArray(data.steps) || data.steps.length === 0) {
        return t('templates.import.errors.noSteps');
    }
    for (const step of data.steps) {
        if (!step.title || typeof step.title !== 'string') {
            return t('templates.import.errors.stepMissingTitle');
        }
        if (!Array.isArray(step.questions) || step.questions.length === 0) {
            return t('templates.import.errors.stepNoQuestions', { title: step.title });
        }
        for (const q of step.questions) {
            if (!q.type || !VALID_QUESTION_TYPES.includes(q.type)) {
                return t('templates.import.errors.invalidType', { type: q.type, types: VALID_QUESTION_TYPES.join(', ') });
            }
            if (!q.label || typeof q.label !== 'string') {
                return t('templates.import.errors.questionMissingLabel', { title: step.title });
            }
        }
    }
    return null;
}

export default function useTemplatesModal({ onSelectTemplate }) {
    const { t } = useTranslation('formsModals');
    const [dragOver, setDragOver] = useState(false);
    const [error, setError] = useState(null);

    const processFile = useCallback((file) => {
        if (!file) return;
        if (!file.name.endsWith('.json') && file.type !== 'application/json') {
            setError(t('templates.import.errors.notJson'));
            return;
        }
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = JSON.parse(e.target.result);
                const validationError = validateTemplateJson(data, t);
                if (validationError) {
                    setError(validationError);
                    return;
                }
                setError(null);
                onSelectTemplate?.(data);
            } catch {
                setError(t('templates.import.errors.unreadable'));
            }
        };
        reader.readAsText(file);
    }, [onSelectTemplate, t]);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        setDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        setDragOver(false);
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setDragOver(false);
        processFile(e.dataTransfer.files[0]);
    }, [processFile]);

    const handleFileInput = useCallback((e) => {
        processFile(e.target.files[0]);
        e.target.value = '';
    }, [processFile]);

    const clearError = useCallback(() => setError(null), []);

    return { dragOver, error, handleDragOver, handleDragLeave, handleDrop, handleFileInput, clearError };
}
